import {
  Button,
  CurrencyInput,
  DatePicker,
  Input,
  StatusPill,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { Copy, ExternalLink, MessageCircle, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { RequestAlert } from '../../app/data-view.js';
import {
  getApiError,
  onlinePaymentsApi,
  salesApi,
  type OnlineCharge,
  type Sale,
  type SimulatedChargeOutcome,
} from '../../lib/api.js';
import { AmountField } from './amount-field.js';
import { basisPointsOf, percentOf, type AmountMode } from './sale-cart.js';
import { paymentTotals } from './sale-payment-totals.js';
import { FormFrame } from './payment-form-frame.js';
import { chargeDescriptionOf } from './sale-charge-description.js';

export function OnlineChargeForm({
  sale,
  today,
  dueDays,
  suggestedCents,
  availableCreditCents = 0,
  onCreditApplied,
  onCancel,
  onCreated,
}: {
  sale: Sale;
  today: string;
  dueDays: number;
  suggestedCents: number;
  availableCreditCents?: number;
  onCreditApplied?: (sale: Sale) => Promise<void>;
  onCancel: () => void;
  onCreated: (charge: OnlineCharge) => Promise<void>;
}) {
  const [amount, setAmount] = useState<number | null>(suggestedCents);
  const [dueOn, setDueOn] = useState<string | null>(addDays(today, dueDays));
  const [description, setDescription] = useState(() => chargeDescriptionOf(sale));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const [creditAmount, setCreditAmount] = useState(0);
  const [creditMode, setCreditMode] = useState<AmountMode>('BRL');
  const [creditBp, setCreditBp] = useState(0);
  const creditLimit = Math.min(availableCreditCents, suggestedCents);
  const creditError =
    creditAmount > creditLimit
      ? 'O valor ultrapassa o crédito disponível ou o que falta pagar nesta venda.'
      : undefined;

  function chooseCredit(cents: number) {
    setCreditAmount(cents);
    setAmount(Math.max(0, suggestedCents - cents));
    setError(undefined);
  }

  async function applyCredit() {
    if (creditAmount <= 0) return setError('Informe quanto crédito deseja usar.');
    if (creditError) return setError(creditError);
    setSaving(true);
    setError(undefined);
    try {
      const next = await salesApi.registerPayment(sale.id, {
        method: 'CREDIT',
        amountCents: creditAmount,
        paidOn: today,
      });
      setAmount(paymentTotals(next).registrableCents);
      setCreditAmount(0);
      setCreditMode('BRL');
      setCreditBp(0);
      await onCreditApplied?.(next);
    } catch (value) {
      setError(getApiError(value));
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    if (creditAmount > 0)
      return setError(creditError ?? 'Clique em Aplicar crédito antes de gerar a cobrança.');
    if (!amount || amount <= 0) return setError('Informe o valor da cobrança.');
    if (amount > suggestedCents)
      return setError(
        `A cobrança passa do que falta cobrar (${formatCurrency(suggestedCents, sale.currency)}). Diminua o valor.`,
      );
    if (!dueOn) return setError('Informe até quando a cliente pode pagar.');
    if (!description.trim()) return setError('Descreva o que o cliente está pagando.');
    setSaving(true);
    setError(undefined);
    try {
      await onCreated(
        await onlinePaymentsApi.create(sale.id, {
          amountCents: amount,
          dueOn,
          description: description.trim(),
        }),
      );
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }

  return (
    <FormFrame
      title="Gerar cobrança"
      error={error}
      saving={saving}
      submitLabel="Gerar cobrança"
      onCancel={onCancel}
      onSubmit={() => void submit()}
    >
      <div className="grid gap-1 text-sm">
        <p className="font-semibold">Pagamento pelo Asaas: Pix, boleto ou cartão à vista</p>
        <p className="text-xs text-content-muted">
          O cliente escolhe como pagar na página do Asaas, pelo link que você enviar. Também poderá
          usar o QR code ou Pix copia e cola. Você não precisa escolher o meio aqui; a confirmação
          vem automaticamente do Asaas após o pagamento. Não há parcelamento nesta cobrança.
        </p>
      </div>
      {availableCreditCents > 0 && onCreditApplied ? (
        <section
          aria-label="Usar crédito antes da cobrança"
          className="grid gap-3 rounded-xl border border-surface-border p-3"
        >
          <p className="text-sm font-semibold">
            Crédito disponível: {formatCurrency(availableCreditCents, sale.currency)}
          </p>
          <AmountField
            label="Valor do crédito"
            mode={creditMode}
            onMode={(mode) => {
              if (mode === 'PCT') setCreditBp(basisPointsOf(creditAmount, availableCreditCents));
              setCreditMode(mode);
            }}
            cents={creditAmount}
            onCents={(cents) => chooseCredit(cents ?? 0)}
            basisPoints={creditBp}
            onBasisPoints={(bp) => {
              setCreditBp(bp);
              chooseCredit(percentOf(availableCreditCents, bp));
            }}
            baseCents={availableCreditCents}
            percentAllowed
            currency={sale.currency}
            hint="O percentual usa o crédito disponível do cliente, não o total da venda."
            error={creditError}
          />
          <p className="text-xs text-content-muted">
            Após usar esse crédito, falta cobrar:{' '}
            <strong>
              {formatCurrency(Math.max(0, suggestedCents - creditAmount), sale.currency)}
            </strong>
            .
          </p>
          <div className="flex justify-start">
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => void applyCredit()}
            >
              Aplicar crédito
            </Button>
          </div>
          <p className="text-xs text-content-muted">
            O crédito só é usado ao clicar em Aplicar crédito. O restante permanece disponível.
          </p>
        </section>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <CurrencyInput
          label="Valor da cobrança"
          currency={sale.currency}
          value={amount}
          onValueChange={setAmount}
          hint={`Falta cobrar ${formatCurrency(Math.max(0, suggestedCents - creditAmount), sale.currency)}${creditAmount > 0 ? ' após aplicar o crédito' : ''}. Pode ser só uma parte.`}
        />
        <DatePicker label="Vence em" value={dueOn} onValueChange={setDueOn} today={today} />
      </div>
      <Input
        label="Descrição para o cliente"
        hint="Informe os serviços, produtos ou pacotes cobrados. Esse texto aparece na cobrança do Asaas."
        value={description}
        maxLength={200}
        onChange={(event) => setDescription(event.target.value)}
      />
    </FormFrame>
  );
}

export function OnlineChargePanel({
  sale,
  charge,
  canManage,
  onBack,
  onChanged,
}: {
  sale: Sale;
  charge: OnlineCharge;
  canManage: boolean;
  onBack: () => void;
  onChanged: (charge: OnlineCharge, message: string) => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      snackbar.success(`${what} copiado.`);
    } catch {
      snackbar.failure(`Não deu para copiar. Selecione o ${what.toLowerCase()} e copie.`);
    }
  }

  async function run(action: () => Promise<OnlineCharge>, message: (next: OnlineCharge) => string) {
    setBusy(true);
    setError(undefined);
    try {
      const next = await action();
      await onChanged(next, message(next));
    } catch (value) {
      setError(getApiError(value));
    } finally {
      setBusy(false);
    }
  }

  /// A janela abre no clique: aberta depois da resposta, o navegador a trataria como pop-up.
  async function whatsapp() {
    const tab = window.open('', '_blank');
    try {
      const { whatsappUrl } = await onlinePaymentsApi.whatsapp(charge.id);
      if (tab && whatsappUrl) {
        tab.opener = null;
        tab.location.href = whatsappUrl;
        snackbar.success('WhatsApp aberto com a cobrança pronta. Confira e envie.');
      } else {
        tab?.close();
        snackbar.failure(
          whatsappUrl
            ? 'O navegador bloqueou a janela do WhatsApp. Libere pop-ups deste endereço e tente de novo.'
            : 'Cliente sem celular cadastrado: copie o Pix ou o link e mande por outro meio.',
        );
      }
    } catch (value) {
      tab?.close();
      snackbar.failure(getApiError(value));
    }
  }

  const simulate = (outcome: SimulatedChargeOutcome) =>
    void run(
      () => onlinePaymentsApi.simulate(charge.id, outcome),
      (next) =>
        next.status === 'PAID'
          ? `Pagamento de ${formatCurrency(next.amountCents, sale.currency)} confirmado.`
          : next.status === 'EXPIRED'
            ? 'Cobrança vencida. O valor voltou para em aberto.'
            : 'Cobrança atualizada.',
    );

  return (
    <section
      aria-label="Cobrança online"
      className="grid gap-4 rounded-2xl border border-brand-500/50 bg-surface-card p-4 sm:grid-cols-[auto_minmax(0,1fr)]"
    >
      {charge.pixQrImage ? (
        <img
          src={charge.pixQrImage}
          alt="QR code do Pix"
          className="size-40 justify-self-center rounded-xl border border-surface-border bg-white p-2"
        />
      ) : null}
      <div className="grid min-w-0 gap-3">
        <div>
          <h3 className="text-base font-semibold">
            Cobrança de {formatCurrency(charge.amountCents, sale.currency)}
          </h3>
          <p className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
            Vence em {formatCivilDate(charge.dueOn)}
            <StatusPill tone="warning">Aguardando pagamento</StatusPill>
          </p>
        </div>
        {charge.pixPayload ? (
          <CopyRow
            label="Pix copia e cola"
            value={charge.pixPayload}
            onCopy={() => void copy(charge.pixPayload!, 'Pix copia e cola')}
          />
        ) : null}
        {charge.invoiceUrl ? (
          <CopyRow
            label="Link de pagamento"
            value={charge.invoiceUrl}
            onCopy={() => void copy(charge.invoiceUrl!, 'Link')}
          />
        ) : null}
        {!charge.pixPayload && !charge.invoiceUrl ? (
          <p className="text-xs text-content-muted">
            O provedor ainda não devolveu o Pix nem o link. Atualize em instantes.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void whatsapp()}>
            <MessageCircle className="size-4" aria-hidden="true" />
            Preparar WhatsApp
          </Button>
          {charge.invoiceUrl ? (
            <a
              href={charge.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-9 items-center gap-1.5 rounded-xl border border-surface-border px-3 text-sm font-medium hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ExternalLink className="size-4" aria-hidden="true" />
              Abrir a fatura
            </a>
          ) : null}
          {charge.environment !== 'SIMULATED' ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                void run(
                  () => onlinePaymentsApi.refresh(charge.id),
                  (next) =>
                    next.status === 'PAID'
                      ? `Pagamento de ${formatCurrency(next.amountCents, sale.currency)} confirmado.`
                      : 'Situação conferida no provedor.',
                )
              }
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              Atualizar situação
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={onBack} disabled={busy}>
            Voltar
          </Button>
        </div>
      </div>
      {charge.environment === 'SIMULATED' && canManage ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-surface-border bg-surface-elevated p-3 sm:col-span-2">
          <p className="min-w-0 flex-1 basis-52 text-xs text-content-muted">
            <strong className="text-content">Cobrança simulada.</strong> Nenhum dinheiro de verdade.
            Simule o que o provedor avisaria:
          </p>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => simulate('PAID_PIX')}>
            Pagou por Pix
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => simulate('PAID_CARD')}>
            Pagou por cartão
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => simulate('OVERDUE')}>
            Venceu
          </Button>
        </div>
      ) : null}
      {error ? (
        <div className="sm:col-span-2">
          <RequestAlert message={error} />
        </div>
      ) : null}
    </section>
  );
}

function CopyRow({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs font-semibold text-content/70">{label}</span>
      <div className="flex min-w-0 items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg bg-surface-elevated px-2.5 py-2 text-xs">
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={onCopy} aria-label={`Copiar ${label}`}>
          <Copy className="size-4" aria-hidden="true" />
          Copiar
        </Button>
      </div>
    </div>
  );
}

export function CancelChargeForm({
  sale,
  charge,
  onCancel,
  onCanceled,
}: {
  sale: Sale;
  charge: OnlineCharge;
  onCancel: () => void;
  onCanceled: (charge: OnlineCharge) => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function submit() {
    setSaving(true);
    setError(undefined);
    try {
      await onCanceled(await onlinePaymentsApi.cancel(charge.id));
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }
  return (
    <FormFrame
      title={`Cancelar a cobrança de ${formatCurrency(charge.amountCents, sale.currency)}`}
      error={error}
      saving={saving}
      submitLabel="Cancelar cobrança"
      onCancel={onCancel}
      onSubmit={() => void submit()}
    >
      <p className="text-xs text-content-muted">
        Ela é cancelada também no provedor, para a cliente não conseguir pagar depois, e o valor
        volta para em aberto. Se a cliente pagou por fora, registre o recebimento em seguida.
      </p>
    </FormFrame>
  );
}

function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
